// types
import { DeliveryAddress, ANNOTATION_ASSETS } from "@swiggy-private/payment-types";
import { ADDRESS_CATEGORIES } from "@swiggy-private/core-types";
import {
    FetchTransitionLottie,
    GetTickScreenLottie,
    LottieMarker,
    OnTransitionError,
    SuperMessage,
    TRANSITION_ERROR_TYPES,
    PostSuccessDelayOptions,
} from "../interfaces";

// helpers
import { getCloudinaryUrl, isEmpty } from "@swiggy-private/js-utils";
import {
    captureError,
    FESTIVE_END_DATE,
    FESTIVE_START_DATE,
    isDateWithinInterval,
    replaceText,
} from "@swiggy-private/core-ui";

// constants
import {
    REWARD_UNLOCKED_WITH_SAVINGS,
    REWARD_UNLOCKED_WITHOUT_SAVINGS,
    TRANSITION_LOTTIE_ASSET_PATH,
    TRANSITION_LOTTIE_BLACK_URL,
    TRANSITION_LOTTIE_POP_URL,
    TRANSITION_LOTTIE_URL,
    FESTIVE_TRANSITION_LOTTIE_URL,
    TRANSITION_LOTTIE_BLACK_OTP_URL,
    BLACK_OTP_LOTTIE_ASSET_IDS,
    TRANSITION_LOTTIE_BOLT_URL,
    TRANSITION_LOTTIE_BOLT_OTP_URL,
    blackBenefitTextTimeout,
    BOLT_TIMEOUT,
    GIFTING_CART_TIMEOUT,
    TRANSITION_LOTTIE_AFFORDABLE_URL,
    AFFORDABLE_CART_M2_TIMEOUT,
    TOING_TRANSITION_LOTTIE_URL,
    TRANSITION_LOTTIE_GIFTING_ORDER_PLACED_URL,
} from "../constants";

export const getSavingsText = (savings?: SuperMessage, isMilestoneUnlockEligible?: boolean) => {
    if (isEmpty(savings)) {
        return isMilestoneUnlockEligible ? replaceText(REWARD_UNLOCKED_WITHOUT_SAVINGS) : "";
    }

    const message = savings?.successMessageInfo || savings?.successMessage;

    if (isEmpty(message)) {
        return isMilestoneUnlockEligible ? replaceText(REWARD_UNLOCKED_WITHOUT_SAVINGS) : "";
    }

    if (isMilestoneUnlockEligible) {
        return replaceText(`${message} ${REWARD_UNLOCKED_WITH_SAVINGS}`);
    }

    return replaceText(message);
};

export const hasInvalidAddressCategory = (address?: DeliveryAddress | null): boolean => {
    return (
        isEmpty(address?.userIntentAddressCategory) ||
        address?.userIntentAddressCategory === ADDRESS_CATEGORIES.ADDRESS_CATEGORY_INVALID
    );
};

export const getAddressAnotationAsset = (
    address?: DeliveryAddress,
    category?: number,
): ANNOTATION_ASSETS => {
    switch (category) {
        case ADDRESS_CATEGORIES.ADDRESS_CATEGORY_HOME:
            return ANNOTATION_ASSETS.HOME;
        case ADDRESS_CATEGORIES.ADDRESS_CATEGORY_WORK:
            return ANNOTATION_ASSETS.WORK;
        case ADDRESS_CATEGORIES.ADDRESS_CATEGORY_FRIENDS_AND_FAMILY:
            return ANNOTATION_ASSETS.HOME;
        case ADDRESS_CATEGORIES.ADDRESS_CATEGORY_OTHER:
        default:
            return ANNOTATION_ASSETS.OTHER;
    }
};

export const fetchTransitionAsset = async (id: string, onError: OnTransitionError): Promise<string | null> => {
    if (isEmpty(id)) {
        onError(TRANSITION_ERROR_TYPES.ASSET_FETCH_ERROR, "Invalid asset id");
        return null;
    }

    const url = getCloudinaryUrl(id);

    try {
        if (isEmpty(url)) {
            onError(TRANSITION_ERROR_TYPES.ASSET_FETCH_ERROR, "Invalid asset url");
            return null;
        }

        const response = await fetch(url);
        const blob = await response.blob();
        return URL?.createObjectURL?.(blob);
    } catch (error) {
        onError(TRANSITION_ERROR_TYPES.ASSET_FETCH_ERROR, error, url);
        return null;
    }
};

export function getTickScreenTransitionLottieUrl({
    isSwiggyBlackApplicable,
    isSwiggyBlackOtpAnimationApplicable,
    isPopCart,
    isBoltCartM3,
    isAffordableCartM2,
    isToingFlow,
    isGiftingCart,
}: GetTickScreenLottie): string | undefined {
    const isFestivePeriod = isDateWithinInterval(new Date(), FESTIVE_START_DATE, FESTIVE_END_DATE);

    if (isGiftingCart) {
        return TRANSITION_LOTTIE_GIFTING_ORDER_PLACED_URL;
    }

    if (isSwiggyBlackApplicable) {
        if (isSwiggyBlackOtpAnimationApplicable && !isBoltCartM3) {
            return TRANSITION_LOTTIE_BLACK_OTP_URL;
        }

        if (isBoltCartM3 && !isSwiggyBlackOtpAnimationApplicable) {
            return TRANSITION_LOTTIE_BOLT_URL;
        }

        if (isBoltCartM3 && isSwiggyBlackOtpAnimationApplicable) {
            return TRANSITION_LOTTIE_BOLT_OTP_URL;
        }

        if (isAffordableCartM2) {
            return TRANSITION_LOTTIE_AFFORDABLE_URL;
        }

        return TRANSITION_LOTTIE_BLACK_URL;
    }

    if (isPopCart) {
        return TRANSITION_LOTTIE_POP_URL;
    }

    if (isFestivePeriod) {
        return FESTIVE_TRANSITION_LOTTIE_URL;
    }

    if (isBoltCartM3) {
        return TRANSITION_LOTTIE_BOLT_URL; // bolt lottie
    }

    if (isAffordableCartM2) {
        return TRANSITION_LOTTIE_AFFORDABLE_URL; // 99 store lottie
    }

    if (isToingFlow) {
        return TOING_TRANSITION_LOTTIE_URL;
    }

    return TRANSITION_LOTTIE_URL;
}

export const fetchTransitionLottie = async ({
    onError,
    deliveryAddressAnotation,
    isSwiggyBlackApplicable,
    isSwiggyBlackOtpAnimationApplicable,
    isPopCart,
    isBoltCartM3,
    isAffordableCartM2,
    isToingFlow,
    isGiftingCart,
}: FetchTransitionLottie): Promise<unknown> => {
    const transitionLottie =
        getTickScreenTransitionLottieUrl({
            isSwiggyBlackApplicable,
            isSwiggyBlackOtpAnimationApplicable,
            isPopCart,
            isBoltCartM3,
            isAffordableCartM2,
            isToingFlow,
            isGiftingCart,
        }) ?? "";
    try {
        const isBlckOTPCase = isSwiggyBlackApplicable && isSwiggyBlackOtpAnimationApplicable;
        const lottieRequest = await fetch(transitionLottie);
        const lottieData = await lottieRequest.json();

        const staticAsset = await fetchTransitionAsset(
            `${TRANSITION_LOTTIE_ASSET_PATH}/${ANNOTATION_ASSETS.STATIC}`,
            onError,
        );

        // update the delivery address anotation
        if (deliveryAddressAnotation && lottieData?.assets?.[0]?.p && lottieData?.assets?.[0]?.u) {
            const addressAsset = await fetchTransitionAsset(
                `${TRANSITION_LOTTIE_ASSET_PATH}/${deliveryAddressAnotation}`,
                onError,
            );
            lottieData.assets[0].p = addressAsset;
            lottieData.assets[0].u = "";
        }

        // update the static asset
        if (staticAsset && lottieData?.assets?.[1]?.p && lottieData?.assets?.[1]?.u) {
            lottieData.assets[1].p = staticAsset;
            lottieData.assets[1].u = "";
        }

        // updates for blck assets
        // [!IMPORTANT] Adding `isBlckOTPCase` condition because there might be cases when lottie has assets at
        // [!IMPORTANT] these positions but we need to update with these assets only when it is BLCK + OTP case
        if (isBlckOTPCase) {
            if (lottieData?.assets?.[2]?.p && lottieData?.assets?.[2]?.u) {
                const starAsset = await fetchTransitionAsset(
                    `${TRANSITION_LOTTIE_ASSET_PATH}/${BLACK_OTP_LOTTIE_ASSET_IDS.STAR}`,
                    onError,
                );
                lottieData.assets[2].p = starAsset;
                lottieData.assets[2].u = "";
            }

            if (lottieData?.assets?.[3]?.p && lottieData?.assets?.[3]?.u) {
                const shimmerAsset = await fetchTransitionAsset(
                    `${TRANSITION_LOTTIE_ASSET_PATH}/${BLACK_OTP_LOTTIE_ASSET_IDS.SHIMMER}`,
                    onError,
                );
                lottieData.assets[3].p = shimmerAsset;
                lottieData.assets[3].u = "";
            }

            if (lottieData?.assets?.[4]?.p && lottieData?.assets?.[4]?.u) {
                const otpStampAsset = await fetchTransitionAsset(
                    `${TRANSITION_LOTTIE_ASSET_PATH}/${BLACK_OTP_LOTTIE_ASSET_IDS.OTP_STAMP}`,
                    onError,
                );
                lottieData.assets[4].p = otpStampAsset;
                lottieData.assets[4].u = "";
            }
        }

        return lottieData;
    } catch (e) {
        captureError(e);
        onError(TRANSITION_ERROR_TYPES.FETCH_FAILED, e, transitionLottie);
    }
};

// can't type lottieData
export const getLottieSoundMarker = (lottieData: any, markerName: string) => {
    if (!isEmpty(lottieData) && !isEmpty(lottieData.markers)) {
        return lottieData.markers.find((marker: LottieMarker) => marker.cm === markerName);
    }
};

export const getPostSuccessDelay = ({
    isSwiggyBlackApplicable = false,
    blackBenefitsTimeout,
    successTimeout,
    isBoltCartM3 = false,
    isAffordableCartM2 = false,
    isGiftingCart = false,
}: PostSuccessDelayOptions): number => {
    if (isGiftingCart) {
        return GIFTING_CART_TIMEOUT;
    }

    if (isBoltCartM3) {
        return BOLT_TIMEOUT;
    }

    if (isAffordableCartM2) {
        return AFFORDABLE_CART_M2_TIMEOUT;
    }

    return isSwiggyBlackApplicable ? blackBenefitTextTimeout(blackBenefitsTimeout) : successTimeout;
};
